import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Album, Book, Movie } from "@shared/types";
import { listAlbums, listBooks, listMovies } from "../api";
import { SafeCover } from "../components/SafeCover";

type MediaType = "all" | "book" | "vinyl" | "movie";

export function LookupPage() {
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState("");
  const [type, setType] = useState<MediaType>("all");

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSearched(query.trim());
  };

  return (
    <div className="space-y-4">
      <form onSubmit={onSubmit} className="space-y-2">
        <label className="block text-sm" htmlFor="lookup-q">
          Search your library
        </label>
        <input
          id="lookup-q"
          type="search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="title, author, artist…"
          className="w-full px-4 py-3 text-lg bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex gap-2 text-sm">
          {(["all", "book", "vinyl", "movie"] as MediaType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`px-3 py-1 rounded ${
                type === t ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              {labelFor(t)}
            </button>
          ))}
        </div>
      </form>

      <Results query={searched} type={type} />
    </div>
  );
}

function labelFor(t: MediaType): string {
  switch (t) {
    case "all":
      return "All";
    case "book":
      return "Books";
    case "vinyl":
      return "Vinyl";
    case "movie":
      return "Movies";
  }
}

function Results({ query, type }: { query: string; type: MediaType }) {
  const showBooks = type === "all" || type === "book";
  const showAlbums = type === "all" || type === "vinyl";
  const showMovies = type === "all" || type === "movie";
  const isBrowse = query === "";

  // When the search field is empty, the Worker's list endpoints return
  // recent rows ordered by `date_modified DESC`, so we get a "browse" view
  // for free. The query key still includes the empty string so cache
  // entries don't collide with searched results.
  const books = useQuery({
    queryKey: ["books", query],
    queryFn: () => listBooks({ q: query, limit: 20 }),
    enabled: showBooks,
  });
  const albums = useQuery({
    queryKey: ["albums", query],
    queryFn: () => listAlbums({ q: query, limit: 20 }),
    enabled: showAlbums,
  });
  const movies = useQuery({
    queryKey: ["movies", query],
    queryFn: () => listMovies({ q: query, limit: 20 }),
    enabled: showMovies,
  });

  const loading =
    (showBooks && books.isLoading) ||
    (showAlbums && albums.isLoading) ||
    (showMovies && movies.isLoading);

  const empty =
    !loading &&
    (!showBooks || (books.data?.books.length ?? 0) === 0) &&
    (!showAlbums || (albums.data?.albums.length ?? 0) === 0) &&
    (!showMovies || (movies.data?.movies.length ?? 0) === 0);

  return (
    <div className="space-y-6">
      {loading && <p className="text-sm text-gray-400">Loading…</p>}
      {empty && (
        <p className="text-sm text-gray-400">
          {isBrowse ? "Nothing in your library yet." : "No matches."}
        </p>
      )}

      {showBooks && books.data && books.data.books.length > 0 && (
        <Section title={`Books (${books.data.total})`}>
          {books.data.books.map((b) => (
            <BookCard key={b.uuid} book={b} />
          ))}
        </Section>
      )}
      {showAlbums && albums.data && albums.data.albums.length > 0 && (
        <Section title={`Vinyl (${albums.data.total})`}>
          {albums.data.albums.map((a) => (
            <AlbumCard key={a.uuid} album={a} />
          ))}
        </Section>
      )}
      {showMovies && movies.data && movies.data.movies.length > 0 && (
        <Section title={`Movies (${movies.data.total})`}>
          {movies.data.movies.map((m) => (
            <MovieCard key={m.uuid} movie={m} />
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">{title}</h2>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function BookCard({ book }: { book: Book }) {
  return (
    <li className="flex gap-3 p-2 bg-gray-800 rounded">
      <SafeCover url={book.cover_url} alt={book.title} className="w-12 h-16 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{book.title}</div>
        <div className="text-xs text-gray-400 truncate">{book.authors.join(", ")}</div>
        {book.shelf && <div className="text-xs text-gray-500">{book.shelf}</div>}
      </div>
    </li>
  );
}

function AlbumCard({ album }: { album: Album }) {
  return (
    <li className="flex gap-3 p-2 bg-gray-800 rounded">
      <div className="w-12 h-16 bg-gray-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{album.title}</div>
        <div className="text-xs text-gray-400 truncate">{album.artist}</div>
        {album.year && <div className="text-xs text-gray-500">{album.year}</div>}
      </div>
    </li>
  );
}

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <li className="flex gap-3 p-2 bg-gray-800 rounded">
      <SafeCover url={movie.cover_url} alt={movie.title} className="w-12 h-16 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{movie.title}</div>
        <div className="text-xs text-gray-400">
          {movie.media_type === "series" ? "Series" : "Movie"}
          {movie.year ? ` • ${movie.year}` : ""}
        </div>
      </div>
    </li>
  );
}
